// Módulos que la plataforma le habilitó a la empresa del usuario (hoy solo el
// Rutificador). Los consumen el menú (qué ítems se ofrecen) y RequireTenantModule
// (quién entra a la ruta). El control de acceso real es del backend: esto decide
// qué se muestra y qué pantalla se abre.
//
// Mismo cache que hooks/useSidebarBranding, a NIVEL DE MÓDULO y guardando la
// PROMESA, por las mismas razones:
//   1. Lo leen a la vez el sidebar desktop, el drawer móvil, el header y el
//      guard de la ruta: la promesa compartida deja una sola request.
//   2. Cruzar /home <-> /admin desmonta y remonta el árbol de rutas entero; un
//      cache en estado de React no sobreviviría a la navegación.
// Además de la promesa se guarda el valor ya resuelto, para que un remontaje
// arranque en "ready" en el mismo render: sin eso, cada cambio de área haría
// parpadear el ítem del menú y el "Cargando…" del guard.
//
// El cache vive lo que dura la pestaña: si la plataforma cambia los módulos de
// una empresa, sus usuarios lo ven al recargar o al volver a iniciar sesión.
import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import { roleRoutes } from "../utils/roles";
import { getMyTenantModules } from "../api/tenantModules";

/** Respuesta vacía: sin tenant, o mientras no hay respuesta del backend. */
const EMPTY = Object.freeze({
  tenantType: null,
  tenantId: null,
  modules: Object.freeze([]),
});

// cacheKey -> { promise, data }. `data` queda en null hasta que la promesa resuelve.
const entries = new Map();

// Un aviso por instancia montada del hook. Cada instancia guarda su propio
// resultado, así que la que quedó en error no se entera sola de que otra (el
// guard, con su "Reintentar") ya consiguió la respuesta: sin este aviso la
// herramienta quedaría abierta sin su ítem en el menú ni su título en el header
// hasta cambiar de área o recargar.
const loadedListeners = new Set();

function notifyLoaded(cacheKey, data) {
  loadedListeners.forEach((listener) => listener(cacheKey, data));
}

/**
 * Tenant del usuario para armar la clave del cache: el operador
 * (`logisticCompanyId`) antes que el comercio (`companyId`), como string o
 * poblado (`{ _id }`). Solo identifica la entrada: qué tenant vale lo decide el
 * backend según el rol, y su respuesta trae el `tenantType` que se usa después.
 */
function tenantKeyOf(user) {
  const logistic = user?.logisticCompanyId?._id ?? user?.logisticCompanyId;
  if (logistic) return `logistic:${logistic}`;
  const company = user?.companyId?._id ?? user?.companyId;
  if (company) return `company:${company}`;
  return "";
}

/**
 * Clave del cache: tenant Y usuario. A diferencia del branding, la respuesta no
 * depende solo del tenant sino del JWT entero (un repartidor recibe 403 donde un
 * admin de la misma empresa recibe sus módulos): si otra persona inicia sesión en
 * la misma pestaña, no puede heredar la respuesta de la anterior.
 *
 * Sin tenant no hay clave: no hay nada que consultar.
 */
function cacheKeyOf(user) {
  const tenantKey = tenantKeyOf(user);
  if (!tenantKey) return "";
  return `${tenantKey}|user:${user?.id ?? user?._id ?? ""}`;
}

function normalize(data) {
  return {
    tenantType: data?.tenantType ?? null,
    tenantId: data?.tenantId ?? null,
    modules: Array.isArray(data?.modules)
      ? data.modules.filter((key) => typeof key === "string")
      : EMPTY.modules,
  };
}

function loadModules(cacheKey) {
  const cached = entries.get(cacheKey);
  if (cached) return cached.promise;

  const entry = { promise: null, data: null };
  entry.promise = getMyTenantModules().then(
    (raw) => {
      entry.data = normalize(raw);
      notifyLoaded(cacheKey, entry.data);
      return entry.data;
    },
    (err) => {
      // Se borra para que un fallo de red no deje la herramienta bloqueada toda
      // la sesión: el siguiente montaje (o retry) vuelve a pedir.
      entries.delete(cacheKey);
      throw err;
    },
  );
  entries.set(cacheKey, entry);
  return entry.promise;
}

/**
 * @returns {{
 *   status: 'loading'|'ready'|'error',
 *   modules: string[],
 *   tenantType: 'logistic'|'company'|null,
 *   tenantId: string|null,
 *   has: (moduleKey: string) => boolean,
 *   retry: () => void,
 * }}
 *
 * - Superadmin del sistema: NO consulta; `status` 'ready', sin módulos ni tenant
 *   (igual que responde el backend) y `has(key)` true para todo: es quien evalúa
 *   y habilita las herramientas.
 * - Sin tenant en el JWT: 'ready' sin módulos, sin request.
 * - `has(key)` solo da true con la respuesta en mano ('ready'): mientras carga o
 *   si falló, falla cerrado.
 */
export default function useTenantModules() {
  const { user, isLoading } = useAuth();
  const isSystemSuperadmin = user?.role === roleRoutes.systemSuperadmin;
  const cacheKey = user && !isSystemSuperadmin ? cacheKeyOf(user) : "";

  // Último resultado de la request, etiquetado con su clave: si cambia el
  // usuario o el tenant, el de la clave anterior no se muestra por el nuevo.
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!cacheKey) return undefined;
    let alive = true;
    const showReady = (data) => {
      if (!alive) return;
      // La misma respuesta llega dos veces a quien la pidió (su propia promesa y
      // el aviso a todas las instancias): la segunda no vuelve a renderizar.
      setResult((prev) =>
        prev?.key === cacheKey && prev.status === "ready" && prev.data === data
          ? prev
          : { key: cacheKey, status: "ready", data },
      );
    };
    const onLoaded = (loadedKey, data) => {
      if (loadedKey === cacheKey) showReady(data);
    };
    loadedListeners.add(onLoaded);
    // En un retry, el error anterior de esta misma clave vuelve a "loading".
    setResult((prev) =>
      prev?.key === cacheKey && prev.status === "error"
        ? { key: cacheKey, status: "loading", data: null }
        : prev,
    );
    loadModules(cacheKey).then(showReady, (err) => {
      console.warn("useTenantModules: no se pudieron leer los módulos del tenant", err);
      if (alive) setResult({ key: cacheKey, status: "error", data: null });
    });
    return () => {
      alive = false;
      loadedListeners.delete(onLoaded);
    };
  }, [cacheKey, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  let status;
  let data = EMPTY;
  if (isSystemSuperadmin) {
    status = "ready";
  } else if (!user) {
    // Sin usuario todavía: el AuthProvider recién montado aún no decodificó el
    // token. Contestar "ready" sin módulos acá haría que el guard expulsara a
    // quien sí tiene el módulo antes de saber quién es.
    status = isLoading ? "loading" : "ready";
  } else if (!cacheKey) {
    status = "ready";
  } else if (result?.key === cacheKey) {
    status = result.status;
    data = result.data ?? EMPTY;
  } else {
    const cached = entries.get(cacheKey)?.data;
    status = cached ? "ready" : "loading";
    data = cached ?? EMPTY;
  }

  const { modules } = data;
  const has = useCallback(
    (moduleKey) => isSystemSuperadmin || (status === "ready" && modules.includes(moduleKey)),
    [isSystemSuperadmin, status, modules],
  );

  return {
    status,
    modules,
    tenantType: data.tenantType,
    tenantId: data.tenantId,
    has,
    retry,
  };
}
